import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from "@mui/material";
import {
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Storage as StorageIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Undo as UndoIcon,
  Edit as EditIcon,
  Restore as RestoreIcon,
  Update as UpdateIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  Build as BuildIcon,
  PlayArrow as PlayArrowIcon,
  CloudUpload as CloudUploadIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { FUNCTIONS } from "../../../Config/firebase";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import YearlyGraphAnalysis from "./components/YearlyGraphAnalysis";
import MonthlyGraphAnalysis from "./components/MonthlyGraphAnalysis";
import UserDataAnalysis from "./components/UserDataAnalysis";
import DataExport from "./components/DataExport";
import MonthlyClosureRollback from "./components/MonthlyClosureRollback";
import DataCorrectionTool from "./components/DataCorrectionTool";
import DataRecoveryTool from "./components/DataRecoveryTool";
import TransactionDateFixer from "./components/TransactionDateFixer";
import MonthlySeedTool from "./components/MonthlySeedTool";
import PerformancePreview from "./components/PerformancePreview";
import BalanceCorrectionTool from "./components/BalanceCorrectionTool";
import PerformanceLedgerDeduplicationTool from "./components/PerformanceLedgerDeduplicationTool";
import TransactionRequestDeduplicationTool from "./components/TransactionRequestDeduplicationTool";
import LedgerStandardizationTool from "./components/LedgerStandardizationTool";
import YieldTestingTool from "./components/YieldTestingTool";
import ProductionMigrationTool from "./components/ProductionMigrationTool";
import FixUserLedgersTool from "./components/FixUserLedgersTool";
import TransactionsVsLedgersTool from "./components/TransactionsVsLedgersTool";
import CollectionCleanUpTool from "./components/CollectionCleanUpTool";
import SafeDeleteVerificationTool from "./components/SafeDeleteVerificationTool";
import FixBalanceFromLedgersTool from "./components/FixBalanceFromLedgersTool";
import ClientReconciliationTool from "./components/ClientReconciliationTool";
import ClientHistoryImportTool from "./components/ClientHistoryImportTool";

const DatabaseAnalysis = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toolSearch, setToolSearch] = useState("");
  const [selectedToolId, setSelectedToolId] = useState(null); // start light (no tool selected)
  const [expandedGroups, setExpandedGroups] = useState({
    Investigations: true,
    Operations: true,
    "Maintenance & Fixes": false,
    "Experimental / Testing": false,
  });

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const validatePassword = httpsCallable(FUNCTIONS, "validateAdminPassword");
      
      const result = await validatePassword({
        password: password.trim(),
        action: "database-analysis"
      });

      if (result.data.valid) {
        setIsAuthenticated(true);
        setError("");
        setSelectedToolId(null);
      } else {
        setError("Invalid password. Access denied. Please check for extra spaces or typos.");
        setPassword(""); // Clear password field for security
      }
    } catch (error) {
      console.error("Error validating password:", error);
      setError("Error validating password. Please try again.");
      setPassword(""); // Clear password field for security
    } finally {
      setLoading(false);
    }
  };

  const TOOL_CATALOG = [
    // Investigations (read-only / discovery)
    {
      id: "client-reconciliation",
      name: "Client Reconciliation",
      group: "Investigations",
      access: "read-only",
      icon: <CheckCircleIcon />,
      description:
        "Compare ledgers vs transection_requests for one client; detect missing links/duplicates; verify net_balance vs last ledger.",
      render: () => <ClientReconciliationTool />,
    },
    {
      id: "transactions-vs-ledgers",
      name: "Transactions vs Ledgers",
      group: "Investigations",
      access: "read-only",
      icon: <AssessmentIcon />,
      description:
        "Investigate if all approved/paid transactions have a matching ledger (read-only).",
      render: () => <TransactionsVsLedgersTool />,
    },
    {
      id: "performance-preview",
      name: "Performance Preview Tool",
      group: "Investigations",
      access: "read-only",
      icon: <AnalyticsIcon />,
      description:
        "Preview how performance data will appear to clients before finalizing monthly closures.",
      render: () => <PerformancePreview />,
    },
    {
      id: "safe-delete-verification",
      name: "Safe Delete Verification",
      group: "Investigations",
      access: "read-only",
      icon: <WarningIcon />,
      description:
        "Verify if it is safe to delete collections by checking for active transactions (status 1–4).",
      render: () => <SafeDeleteVerificationTool />,
    },
    {
      id: "user-data-analysis",
      name: "User Data Analysis",
      group: "Investigations",
      access: "read-only",
      icon: <VisibilityIcon />,
      description:
        "Analyze user account integrity and identify incomplete/invalid profiles (no writes).",
      render: () => <UserDataAnalysis />,
    },
    {
      id: "yearly-graph-analysis",
      name: "Yearly Graph Analysis",
      group: "Investigations",
      access: "read-only",
      icon: <AnalyticsIcon />,
      description:
        "Analyze yearly performance graph data; check for duplicates and validate point calculations.",
      render: () => <YearlyGraphAnalysis />,
    },
    {
      id: "monthly-graph-analysis",
      name: "Monthly Graph Analysis",
      group: "Investigations",
      access: "read-only",
      icon: <StorageIcon />,
      description:
        "Analyze monthly performance data for individual users; validate calculations and find inconsistencies.",
      render: () => <MonthlyGraphAnalysis />,
    },

    // Operations (expected flows / controlled writes)
    {
      id: "client-history-import",
      name: "Client History Import",
      group: "Operations",
      access: "writes",
      icon: <CloudUploadIcon />,
      description:
        "Upload extracted JSON (contract + optional legacy events), preview diffs, apply to new collections with backupId + rollback.",
      render: () => <ClientHistoryImportTool />,
    },
    {
      id: "monthly-closure-rollback",
      name: "Monthly Closure Rollback",
      group: "Operations",
      access: "writes",
      icon: <UndoIcon />,
      description:
        "Safely rollback monthly closures and restore user balances (supports full rollback cleanup).",
      render: () => <MonthlyClosureRollback />,
    },
    {
      id: "production-migration",
      name: "Production Migration",
      group: "Operations",
      access: "writes",
      icon: <CloudUploadIcon />,
      description:
        "Migrate data from production to Rowy with date range, collection selection, filtering, and visual preview.",
      render: () => <ProductionMigrationTool />,
    },
    {
      id: "data-export",
      name: "Data Export & Backup",
      group: "Operations",
      access: "writes",
      icon: <DownloadIcon />,
      description: "Export collections to JSON/CSV for backups before major operations.",
      render: () => <DataExport />,
    },

    // Maintenance & Fixes (dangerous / bulk / cleanup)
    {
      id: "fix-user-ledgers",
      name: "Fix User Ledgers",
      group: "Maintenance & Fixes",
      access: "writes",
      icon: <BuildIcon />,
      description:
        "Correct old ledger categorization (Dec 2024 backwards). Analyse → Apply (backup) → Undo (rollback).",
      render: () => <FixUserLedgersTool />,
    },
    {
      id: "fix-balance-from-ledgers",
      name: "Fix Balance From Ledgers",
      group: "Maintenance & Fixes",
      access: "writes",
      icon: <BuildIcon />,
      description:
        "Mass-fix users.net_balance based on latest ledger balances (bulk operation).",
      render: () => <FixBalanceFromLedgersTool />,
    },
    {
      id: "balance-correction",
      name: "Balance Correction Tool",
      group: "Maintenance & Fixes",
      access: "writes",
      icon: <WarningIcon />,
      description: "Analyze and correct balance discrepancies (preview first).",
      render: () => <BalanceCorrectionTool />,
    },
    {
      id: "data-correction",
      name: "Data Correction Tool",
      group: "Maintenance & Fixes",
      access: "writes",
      icon: <EditIcon />,
      description:
        "Correct percentage values and synchronize data across collections.",
      render: () => <DataCorrectionTool />,
    },
    {
      id: "data-recovery",
      name: "Data Recovery Tool",
      group: "Maintenance & Fixes",
      access: "writes",
      icon: <RestoreIcon />,
      description:
        "Recover and restore data from backups or previous states (use with caution).",
      render: () => <DataRecoveryTool />,
    },
    {
      id: "transaction-date-fixer",
      name: "Transaction Date Fixer",
      group: "Maintenance & Fixes",
      access: "writes",
      icon: <UpdateIcon />,
      description:
        "Fix chronological ordering of recovered transactions by updating dates to match original closure dates.",
      render: () => <TransactionDateFixer />,
    },
    {
      id: "ledger-standardization",
      name: "Ledger Standardization Tool",
      group: "Maintenance & Fixes",
      access: "writes",
      icon: <BuildIcon />,
      description:
        "Standardize old ledgers; convert orphan deposit/withdrawal ledgers to performance where appropriate.",
      render: () => <LedgerStandardizationTool />,
    },
    {
      id: "performance-ledger-dedup",
      name: "Performance Ledger Deduplication",
      group: "Maintenance & Fixes",
      access: "writes",
      icon: <DeleteIcon />,
      description:
        "Identify and remove duplicate performance ledgers safely (preview before deletion).",
      render: () => <PerformanceLedgerDeduplicationTool />,
    },
    {
      id: "txn-request-dedup",
      name: "Transaction Request Deduplication",
      group: "Maintenance & Fixes",
      access: "writes",
      icon: <DeleteIcon />,
      description:
        "Remove duplicate performance transaction requests from transection_requests (CRITICAL).",
      render: () => <TransactionRequestDeduplicationTool />,
    },
    {
      id: "collection-cleanup",
      name: "Collection Clean Up",
      group: "Maintenance & Fixes",
      access: "writes",
      icon: <DeleteIcon />,
      description:
        "Compare Production vs Rowy, identify duplicates/test data, and safely delete Rowy-only documents.",
      render: () => <CollectionCleanUpTool />,
    },

    // Experimental / Testing
    {
      id: "monthly-seed",
      name: "Monthly Seed Tool",
      group: "Experimental / Testing",
      access: "writes",
      icon: <StorageIcon />,
      description: "Seed or initialize monthly data for testing/setup (use with caution).",
      render: () => <MonthlySeedTool />,
    },
    {
      id: "yield-testing",
      name: "Yield Testing Tool",
      group: "Experimental / Testing",
      access: "writes",
      icon: <PlayArrowIcon />,
      description:
        "Simulate participation days for yield testing without waiting real days to pass.",
      render: () => <YieldTestingTool />,
    },
  ].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  const normalizedSearch = toolSearch.trim().toLowerCase();
  const filteredTools = TOOL_CATALOG.filter((t) => {
    if (!normalizedSearch) return true;
    return (
      t.name.toLowerCase().includes(normalizedSearch) ||
      t.description.toLowerCase().includes(normalizedSearch) ||
      t.group.toLowerCase().includes(normalizedSearch)
    );
  });

  const toolsByGroup = filteredTools.reduce((acc, tool) => {
    acc[tool.group] = acc[tool.group] || [];
    acc[tool.group].push(tool);
    return acc;
  }, {});

  const selectedTool = TOOL_CATALOG.find((t) => t.id === selectedToolId) || null;

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
        }}
      >
        <Paper
          elevation={24}
          sx={{
            p: 4,
            width: 400,
            borderRadius: 3,
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            border: "1px solid rgba(42, 245, 152, 0.2)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <SecurityIcon sx={{ fontSize: 64, color: "#2af598", mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: "#fff" }}>
              Database Analysis
            </Typography>
            <Typography variant="body1" sx={{ color: "#aaa" }}>
              Admin Access Required
            </Typography>
          </Box>

          <form onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              label="Access Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              variant="outlined"
              autoFocus
              error={!!error}
              helperText={error}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "#2af598",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2af598",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#aaa",
                  "&.Mui-focused": {
                    color: "#2af598",
                  },
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 2, 
                py: 1.5,
                background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                "&:hover": {
                  background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  opacity: 0.9,
                }
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Access Database"}
            </Button>
          </form>

          {error && (
            <Alert severity="error" sx={{ mt: 2, backgroundColor: "rgba(245, 42, 45, 0.1)", color: "#f52a2d" }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3,
      background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
      minHeight: "100vh",
      paddingTop: { xs: '60px', sm: "0px" }
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin/dashboard")}
            sx={{ 
              mr: 2, 
              minWidth: "auto",
              color: "#fff",
              borderColor: "rgba(42, 245, 152, 0.3)",
              "&:hover": {
                borderColor: "#2af598",
                backgroundColor: "rgba(42, 245, 152, 0.1)"
              }
            }}
          >
            Back to Admin
          </Button>
          <AnalyticsIcon sx={{ fontSize: 40, color: "#2af598", mr: 2 }} />
          <Typography variant="h4" component="h1" sx={{ color: "#fff" }}>
            Database Analysis
          </Typography>
          <Chip
            label="Admin Access"
            sx={{ 
              ml: 2,
              backgroundColor: "rgba(0, 200, 32, 0.2)",
              color: "#00c820",
              border: "1px solid rgba(0, 200, 32, 0.3)"
            }}
            size="small"
          />
        </Box>
        <Typography variant="body1" sx={{ color: "#aaa" }}>
          Comprehensive database analysis, cleanup, and management tools
        </Typography>
      </Box>

      {/* Tool Finder + Tool Content */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          <Paper
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
              position: { md: "sticky" },
              top: { md: 16 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <InfoIcon sx={{ color: "#2af598" }} />
              <Typography variant="h6" sx={{ color: "#2af598" }}>
                Tool Finder
              </Typography>
            </Box>

            <TextField
              fullWidth
              size="small"
              value={toolSearch}
              onChange={(e) => setToolSearch(e.target.value)}
              placeholder="Search by name, category, or purpose…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#777" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": { color: "#eee", fontSize: "1rem" },
                "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                "&:hover fieldset": { borderColor: "rgba(42, 245, 152, 0.4)" },
              }}
            />

            <Alert severity="info" sx={{ mb: 2, "& .MuiAlert-message": { fontSize: "0.95rem" } }}>
              This page opens light. Pick a tool below.
            </Alert>

            {Object.keys(toolsByGroup).length === 0 && (
              <Alert severity="warning">No tools match this search.</Alert>
            )}

            {["Investigations", "Operations", "Maintenance & Fixes", "Experimental / Testing"]
              .filter((g) => toolsByGroup[g]?.length)
              .map((groupName) => (
                <Accordion
                  key={groupName}
                  expanded={!!expandedGroups[groupName]}
                  onChange={(_, expanded) =>
                    setExpandedGroups((s) => ({ ...s, [groupName]: expanded }))
                  }
                  sx={{
                    backgroundColor: "transparent",
                    color: "#eee",
                    border: "1px solid rgba(255,255,255,0.08)",
                    mb: 1,
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#aaa" }} />}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.02rem" }}>
                        {groupName}
                      </Typography>
                      <Chip
                        size="small"
                        label={toolsByGroup[groupName].length}
                        sx={{ color: "#aaa" }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <List dense disablePadding>
                      {toolsByGroup[groupName]
                        .slice()
                        .sort((a, b) =>
                          a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
                        )
                        .map((tool) => (
                          <ListItemButton
                            key={tool.id}
                            selected={selectedToolId === tool.id}
                            onClick={() => setSelectedToolId(tool.id)}
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              "&.Mui-selected": {
                                backgroundColor: "rgba(42, 245, 152, 0.12)",
                                "&:hover": {
                                  backgroundColor: "rgba(42, 245, 152, 0.18)",
                                },
                              },
                              "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 34, color: "#aaa" }}>
                              {tool.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Typography sx={{ color: "#fff", fontSize: "1.02rem", fontWeight: 600 }}>
                                    {tool.name}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={tool.access === "read-only" ? "read-only" : "writes"}
                                    sx={{
                                      height: 22,
                                      fontSize: "0.78rem",
                                      bgcolor:
                                        tool.access === "read-only"
                                          ? "rgba(42,245,152,0.15)"
                                          : "rgba(255,179,0,0.18)",
                                      color:
                                        tool.access === "read-only" ? "#2af598" : "#ffb300",
                                    }}
                                  />
                                </Box>
                              }
                              secondary={
                                <Typography sx={{ color: "#9a9a9a", fontSize: "0.92rem", lineHeight: 1.25 }}>
                                  {tool.description}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <Paper
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
              minHeight: 500,
            }}
          >
            {!selectedTool ? (
              <Box>
                <Typography variant="h6" sx={{ color: "#2af598", mb: 1 }}>
                  Choose a tool
                </Typography>
                <Typography sx={{ color: "#aaa", mb: 2, fontSize: "1rem" }}>
                  Use the left panel to search/browse by category. Each tool shows a short description so you don’t need to remember names.
                </Typography>
                <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.08)" }} />
                <Alert severity="info" sx={{ "& .MuiAlert-message": { fontSize: "0.95rem" } }}>
                  Tip: search for keywords like <b>ledger</b>, <b>rollback</b>, <b>import</b>, <b>dedup</b>, <b>balance</b>.
                </Alert>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ color: "#2af598" }}>
                      {selectedTool.name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#aaa", fontSize: "1rem" }}>
                      {selectedTool.description}
                    </Typography>
                    <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Chip label={selectedTool.group} size="small" sx={{ fontSize: "0.8rem" }} />
                      <Chip
                        label={selectedTool.access === "read-only" ? "read-only" : "writes"}
                        size="small"
                        sx={{
                          bgcolor:
                            selectedTool.access === "read-only"
                              ? "rgba(42,245,152,0.15)"
                              : "rgba(255,179,0,0.18)",
                          color: selectedTool.access === "read-only" ? "#2af598" : "#ffb300",
                          fontSize: "0.8rem",
                        }}
                      />
                    </Box>
                  </Box>

                  <Button
                    variant="outlined"
                    onClick={() => setSelectedToolId(null)}
                    sx={{
                      color: "#fff",
                      borderColor: "rgba(42, 245, 152, 0.3)",
                      "&:hover": {
                        borderColor: "#2af598",
                        backgroundColor: "rgba(42, 245, 152, 0.1)",
                      },
                      whiteSpace: "nowrap",
                    }}
                  >
                    Back to tools
                  </Button>
                </Box>

                <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,0.08)" }} />

                {selectedTool.render()}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DatabaseAnalysis;
